import React, { useState, useEffect } from 'react';
import { CircularProgress, IconButton } from '@mui/material';
import { Email, Lock, CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';

import PrimaryButton from '../../common/buttons/PrimaryButton';
import InputField from '../../common/fields/InputField';
import OtpInput from '../../common/fields/OtpInput';
import {
  forgotPasswordRequest,
  resetPasswordRequest,
  resendOtpRequest,
} from '../../../api/auth.api';

const RESEND_COOLDOWN = 60;

const STEP = { EMAIL: 0, OTP: 1, NEW_PASSWORD: 2, SUCCESS: 3 };

export default function ForgotPassword({ onBackToLogin }) {
  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [passwords, setPasswords] = useState({
    password: '',
    password_confirmation: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [countdown]);

  const handleSendOtp = async () => {
    if (!email) {
      setError('Please enter your email.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await forgotPasswordRequest({ email });
      setStep(STEP.OTP);
      setCountdown(RESEND_COOLDOWN);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError('');
    try {
      await resendOtpRequest({ email, type: 'password_reset' });
      setCountdown(RESEND_COOLDOWN);
      setOtp('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = () => {
    if (otp.length !== 6) {
      setError('Enter the full 6-digit code.');
      return;
    }
    setError('');
    setStep(STEP.NEW_PASSWORD);
  };

  const handleResetPassword = async () => {
    if (!passwords.password || passwords.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (passwords.password !== passwords.password_confirmation) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await resetPasswordRequest({ email, code: otp, ...passwords });
      setStep(STEP.SUCCESS);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. Try again.');
      // If the OTP wrong or expired, go back to OTP step
      if (err.response?.status === 422) setStep(STEP.OTP);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      {/* Enter email */}
      {step === STEP.EMAIL && (
        <>
          <h2 className="text-3xl font-bold text-white mb-2 text-center">Forgot Password</h2>
          <p className="text-gray-400 text-center mb-8">
            Enter your email and we'll send you a reset code.
          </p>

          {error && <ErrorBox message={error} />}

          <div className="space-y-5">
            <InputField
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              disabled={loading}
              slotProps={{
                input: {
                  startAdornment: <Email sx={{ color: '#F6911D', mr: 1 }} />,
                },
              }}
            />
            <PrimaryButton fullWidth onClick={handleSendOtp} disabled={loading}>
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Send Code'}
            </PrimaryButton>
          </div>

          <div className="mt-5 text-center">
            <button
              onClick={onBackToLogin}
              className="text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors"
            >
              ← Back to Login
            </button>
          </div>
        </>
      )}

      {/* Enter OTP */}
      {step === STEP.OTP && (
        <>
          <h2 className="text-3xl font-bold text-white mb-2 text-center">Enter Code</h2>
          <p className="text-gray-400 text-center mb-1">We sent a code to</p>
          <p className="text-orange-400 text-center font-semibold mb-8">{email}</p>

          <OtpInput value={otp} onChange={setOtp} disabled={loading} />

          {error && <ErrorBox message={error} className="mt-4" />}

          <div className="mt-6 space-y-3">
            <PrimaryButton fullWidth onClick={handleVerifyOtp} disabled={otp.length !== 6}>
              Continue
            </PrimaryButton>

            <div className="text-center text-sm text-gray-400">
              {countdown > 0 ? (
                <span className="text-gray-500">Resend in {countdown}s</span>
              ) : (
                <button
                  onClick={handleResend}
                  disabled={loading}
                  className="text-orange-400 hover:text-orange-300 font-medium disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Sending...' : 'Resend code'}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* New password */}
      {step === STEP.NEW_PASSWORD && (
        <>
          <h2 className="text-3xl font-bold text-white mb-2 text-center">New Password</h2>
          <p className="text-gray-400 text-center mb-8">Choose a strong new password.</p>

          {error && <ErrorBox message={error} />}

          <div className="space-y-5">
            <InputField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={passwords.password}
              onChange={(e) => {
                setPasswords({ ...passwords, password: e.target.value });
                setError('');
              }}
              disabled={loading}
              slotProps={{
                input: {
                  startAdornment: <Lock sx={{ color: '#F6911D', mr: 1 }} />,
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: 'rgba(255,255,255,0.6)' }}
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                },
              }}
            />
            <InputField
              label="Confirm Password"
              type={showConfirm ? 'text' : 'password'}
              value={passwords.password_confirmation}
              onChange={(e) => {
                setPasswords({
                  ...passwords,
                  password_confirmation: e.target.value,
                });
                setError('');
              }}
              disabled={loading}
              slotProps={{
                input: {
                  startAdornment: <Lock sx={{ color: '#F6911D', mr: 1 }} />,
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowConfirm(!showConfirm)}
                      edge="end"
                      sx={{ color: 'rgba(255,255,255,0.6)' }}
                      tabIndex={-1}
                    >
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                },
              }}
            />
            <PrimaryButton fullWidth onClick={handleResetPassword} disabled={loading}>
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Reset Password'}
            </PrimaryButton>
          </div>
        </>
      )}

      {/* Success */}
      {step === STEP.SUCCESS && (
        <div className="text-center py-6">
          <div className="flex justify-center mb-4">
            <CheckCircle sx={{ fontSize: 64, color: '#22c55e' }} />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Password Reset!</h2>
          <p className="text-gray-400 mb-8">Your password has been updated. You can now log in.</p>
          <PrimaryButton fullWidth onClick={onBackToLogin}>
            Back to Login
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}

function ErrorBox({ message, className = '' }) {
  return (
    <div className={`p-3 bg-red-500/10 border border-red-500/50 rounded-lg mb-4 ${className}`}>
      <p className="text-red-400 text-sm text-center">{message}</p>
    </div>
  );
}
